import { additionalDataType, appendVariablesMap, initializeVariablesMap, stepNames, variables } from '@repo/onb-api';

/** A step initialized before it mounts (by the server page or by the submit that led to it) */
export type initializedStep = {
  stepName: stepNames;
  /** Variables returned by the step initialization */
  variables?: variables;
  /** Root variables appended to the flow before initializing it */
  appendedVariables?: variables;
};

/**
 * Returns an object with mapped initialize variables based on the provided step name and variables.
 *
 * @param {stepNames} name - The name of the step.
 * @param {variables} variables - The variables to be consumed.
 * @return {variables} An object with initialize variables.
 */
export const getInitializeVariables = ({ name, variables }: { name: stepNames; variables?: variables }) => {
  return initializeVariablesMap[name]?.reduce((acc, { local: localVars, remote }) => {
    const local = localVars.find((v) => variables?.[v] !== undefined);
    return { ...acc, [remote]: local ? variables?.[local] : undefined };
  }, {} as variables);
};

/**
 * What initializing a step takes: the local variables appended to the flow first, and the variables the
 * initialization sends. Shared by the client (CheckClientData) and the server, so both send the same request.
 *
 * @param {stepNames} stepName - The step to initialize.
 * @param {variables} [rootVariables] - The flow root variables.
 * @param {additionalDataType} additionalData - The flow additional data.
 * @return {{ appendedVariables?: variables; initVariables?: variables }} Variables to append and to send.
 */
export const getStepInitRequest = (
  stepName: stepNames,
  rootVariables: variables | undefined,
  additionalData: additionalDataType,
) => {
  const appendedVariables = (appendVariablesMap(stepName, additionalData) as variables | null) || undefined;
  const initVariables = getInitializeVariables({
    name: stepName,
    variables: appendedVariables ? { ...rootVariables, ...appendedVariables } : rootVariables,
  });
  return { appendedVariables, initVariables };
};
